import styled from "@emotion/styled";
import { type Opportunity } from "@repo/api";
import {
  Badge,
  Button,
  LightMode,
  Text,
  Variables,
  aiGeneratedIcon,
} from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import { localizeDateAndTime } from "@repo/ui";

import { useSummaryHeaderConfig } from "./hooks";

const HeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${Variables.space100};
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Variables.space25};
`;

interface ProjectSummaryHeaderProps {
  opportunity: Opportunity;
  isGenerating: boolean;
  onGenerate: () => void;
}

export const ProjectSummaryHeader = ({
  opportunity,
  isGenerating,
  onGenerate,
}: ProjectSummaryHeaderProps) => {
  const { dateFnsLocale } = useTranslation();
  const summaryHeaderConfig = useSummaryHeaderConfig(opportunity);
  const hasSummary = Boolean(opportunity.aiProjectData?.summaryGeneratedAt);
  const generateButtonTitle = hasSummary
    ? t("action.regenerate_summary")
    : t("action.generate_summary");

  const badgeText = opportunity.aiProjectData?.summaryGeneratedAt
    ? `${t("label.last_generated")}: ${localizeDateAndTime(
        opportunity.aiProjectData.summaryGeneratedAt,
        dateFnsLocale
      )}`
    : t("prompt.new_summary_can_be_generated");
  return (
    <HeaderContainer>
      <TitleContainer>
        {summaryHeaderConfig.text ? (
          <Text textStyle="Heading.XS" color={LightMode.colourTextAndIcon1}>
            {summaryHeaderConfig.text}
          </Text>
        ) : null}
        <Badge
          colour={summaryHeaderConfig.color}
          label={badgeText}
          size="small"
        />
      </TitleContainer>

      <Button
        title={generateButtonTitle}
        size="small"
        type="primary"
        onClick={onGenerate}
        disabled={isGenerating || !summaryHeaderConfig.canGenerate}
        loading={isGenerating}
        iconOnly
        ssrIcon={aiGeneratedIcon}
      />
    </HeaderContainer>
  );
};
